/* Copyright (c) 2017 Backpack Health LLC */
/* global IS_ANDROID_BUILD */

import { get as _get } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { injectIntl, FormattedMessage } from 'react-intl';

import List, { CardListItem } from '../../../../components/List';
import { Grid, Cell } from '../../../../components/Grid';
import { BirthDateWithAge } from '../../../../localization/LocalizedDate';
import LocalizedGender from '../../../presenters/LocalizedGender.redux';
import LocalizedBirthSex from '../../../presenters/LocalizedBirthSex.redux';
import LocalizedBloodType from '../../../presenters/LocalizedBloodType.redux';
import LocalizedRace from '../../../presenters/LocalizedRace.redux';
import { LocalizedResidence } from '../../../../localization/LocalizedResidence';

import { Text } from '../../../../../bphComponents/bundle';
import ProfileModel from '../../../../../models/ProfileModel';
import GenderModel from '../../../../../models/GenderModel';
import DateUtil from '../../../..//../utilities/DateUtil';
import FilePickerRow from '../../../../components/modal/addMenu/FilePickerRow';
import FilePickerModal from '../../../../components/modal/FilePickerModal';
import RouteNames from '../..//../RouteNames';
import ProfileAvatar from '../../../../components/ProfileAvatar';
import * as MobileUtil from '../../../../../utilities/MobileUtil';
import Card, { GrayCard, CardSection, EmptyCard } from '../../../../components/card/Card';
import FieldValue from '../../../../components/FieldValue';
import { TextLink } from '../../../../components/TextLink';
import ContentLoader from '../../../../components/ContentLoader';
import ContentWrapper from '../../../../components/ContentWrapper';
import { PermissionsRequiredModal, requiredPermissionTypes } from '../../../../components/modal/WarningModal';
import { actions as systemModalActions } from '../../../../redux/ducks/systemModal';

import ProfileService from '../../../../../apiServices/ProfileService';

import './ProfileInfo.styles.scss';

class ProfileInfo extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showAvatarFileModal: false,
      isLoading: false,
    };
  }

  UNSAFE_componentWillMount() {
    if (this.props.getInitialData) {
      this.props.getInitialData();
    }
  }

  async downloadExport() {
    this.setState({ isLoading: true }, async () => {
      try {
        const profileId = _get(this.props, 'profile.data.id');
        const url = await ProfileService.downloadExport(profileId);
        if (url) {
          const link = document.createElement('a');
          link.href = url;
          link.click();
        }
      } finally {
        this.setState({ isLoading: false });
      }
    });
  }

  renderAvatarEditing() {
    const labelPhrase = this.props.profile.hasAvatarPhoto() ? 'profile.editPhoto.button.label' : 'profile.addPhoto.button.label';

    return this.props.profile.hasAvatarPhoto() || MobileUtil.isMobileApplication() ? (
      <CardListItem
        onClick={() => {
          this.setState({ showAvatarFileModal: true });
        }}
      >
        <Grid alignItems="center" spacing="small">
          <Cell>
            <ProfileAvatar profile={this.props.profile} size={54} />
          </Cell>
          <Cell>
            <div className="cmp-text-caption font-color-profile-accent" style={{ marginLeft: '10px' }}>
              <FormattedMessage id={labelPhrase} />
            </div>
          </Cell>
        </Grid>
      </CardListItem>
    ) : (
      <CardListItem>
        <FilePickerRow
          name="profilePhoto"
          accept="image/*"
          onChange={(d) => {
            this.props.actions.selectPhoto(d, () => {
              this.setState({ showAvatarFileModal: false });
            });
          }}
          style={{ cursor: 'pointer' }}
        >
          <Grid alignItems="center" spacing="small">
            <Cell>
              <ProfileAvatar profile={this.props.profile} size={54} />
            </Cell>
            <Cell>
              <div className="cmp-text-caption font-color-profile-accent" style={{ marginLeft: '10px' }}>
                <FormattedMessage id={labelPhrase} />
              </div>
            </Cell>
          </Grid>
        </FilePickerRow>
      </CardListItem>
    );
  }

  renderDeleteButton = () => {
    const { isShared, isMainProfile } = this.props;

    if (isMainProfile) { return null; }

    if (isShared) {
      return (
        <EmptyCard className="font-footnote font-color-gsMedDark" style={{ marginTop: 24 }}>
          <FormattedMessage id="profile.sharedCannotDelete.message" />
        </EmptyCard>
      );
    } else {
      return (
        <EmptyCard style={{ marginTop: 24 }}>
          <TextLink className="font-footnote font-color-gsMedDark" onClick={this.props.deleteProfile} style={{ textDecoration: 'underline' }}>
            <FormattedMessage id="profile.delete.button.label" />
          </TextLink>
        </EmptyCard>
      );
    }
  }

  render() {
    const { profile } = this.props;

    return (
      <ContentLoader isContentLoaded={!this.state.isLoading} coverWholeScreen={true} transparentChildren={true}>
        <ContentWrapper>
          <Card>
            <List hasBorder>
              {this.renderAvatarEditing()}

              <CardListItem onClick={() => { this.props.actions.goToProfilePage(RouteNames.profileName()); }}>
                <FieldValue title="profile.name.field.label">
                  {profile.fullName()}
                </FieldValue>
              </CardListItem>

              <CardListItem onClick={() => { this.props.actions.goToProfilePage(RouteNames.profileDateOfBirth()); }}>
                <FieldValue title="profile.DOB.field.label">
                  <BirthDateWithAge
                    profile={profile.data}
                    today={DateUtil.today()}
                  />
                </FieldValue>
              </CardListItem>

              <CardListItem onClick={() => { this.props.actions.goToProfilePage(RouteNames.profileGender()); }}>
                <FieldValue title="profile.gender.field.label">
                  <LocalizedGender profile={profile} />
                </FieldValue>

                <div>
                  <LocalizedBirthSex profile={profile}>
                    {birthSex => (
                      <div className="font-footnote font-color-gsDark">
                        <FormattedMessage
                          id={this.props.phraseData.birthSexLabel.id}
                          values={{ string1: birthSex }}
                        />
                      </div>
                    )}
                  </LocalizedBirthSex>
                </div>
              </CardListItem>

              <CardListItem onClick={() => { this.props.actions.goToProfilePage(RouteNames.profileResidence()); }}>
                {
                  profile.hasValidResidence() ?
                    <FieldValue title="profile.residence.field.label">
                      <LocalizedResidence
                        profile={profile}
                        primaryClass="font-body"
                        secondaryClass="font-caption"
                      />
                    </FieldValue>
                    :
                    <div className="font-caption font-color-profile-accent">
                      <FormattedMessage
                        id={this.props.phraseData.residenceLabel.id}
                        values={{ string1: profile.greetingName() }}
                      />
                    </div>
                }
              </CardListItem>

              <CardListItem onClick={() => { this.props.actions.goToProfilePage(RouteNames.profileRace()); }}>
                {
                  profile.hasValidRace() ?
                    <FieldValue title="profile.race.field.label">
                      <LocalizedRace
                        profile={profile}
                        primaryClass="font-body"
                        secondaryClass="font-caption font-color-gsDark"
                      />
                    </FieldValue>
                    :
                    <div className="font-caption font-color-profile-accent">
                      <FormattedMessage
                        id={this.props.phraseData.raceLabel.id}
                        values={{ string1: profile.greetingName() }}
                      />
                    </div>
                }
              </CardListItem>

              <CardListItem onClick={() => { this.props.actions.goToProfilePage(RouteNames.profileBloodType()); }}>
                {
                  profile.hasValidBloodType() ?
                    <FieldValue title="profile.bloodType.field.label">
                      <LocalizedBloodType profile={profile} />
                    </FieldValue>
                    :
                    <div className="font-caption font-color-profile-accent">
                      <FormattedMessage
                        id={this.props.phraseData.bloodTypeLabel.id}
                        values={{ string1: profile.greetingName() }}
                      />
                    </div>
                }
              </CardListItem>
            </List>

            <FilePickerModal
              isVisible={this.state.showAvatarFileModal}
              onSelectFileAction={IS_ANDROID_BUILD ? null : (rawFile) => {
                this.props.actions.selectPhoto(rawFile, () => {
                  this.setState({ showAvatarFileModal: false });
                });
              }}
              onTakeImageAction={IS_ANDROID_BUILD ? () => {
                MobileUtil.checkAndRequestPhotoCameraPermissions(
                  () => {
                    this.props.actions.selectMobilePhoto('image', () => {
                      this.setState({ showAvatarFileModal: false });
                    });
                  },
                  () => {
                  },
                  () => {
                    this.props.dispatch(systemModalActions.push(<PermissionsRequiredModal permissions={[requiredPermissionTypes.library]} onDismiss={() => { this.setState({ showAvatarFileModal: false }); }} />));
                  },
                );
              } : null}
              onSelectLibraryAction={IS_ANDROID_BUILD ? () => {
                MobileUtil.checkAndRequestFilePermissions(
                  () => {
                    this.props.actions.selectMobilePhoto('library', () => {
                      this.setState({ showAvatarFileModal: false });
                    });
                  },
                  () => {},
                  () => {
                    this.props.dispatch(systemModalActions.push(<PermissionsRequiredModal permissions={[requiredPermissionTypes.library]} onDismiss={() => { this.setState({ showAvatarFileModal: false }); }} />));
                  },
                );
              } : null}
              onDeleteFileAction={
                profile.hasAvatarPhoto()
                  ? () => {
                    this.props.actions.deletePhoto();
                    this.setState({
                      showAvatarFileModal: false,
                    });
                  }
                  : null
              }
              onCancelAction={() => {
                this.setState({ showAvatarFileModal: false });
              }}
            />
          </Card>

          <button onClick={() => this.downloadExport() } className={classnames('profile-info-export-button cmp-button--primary font-body profile-background-color font-color-white cmp-button-wide')}>
            <FormattedMessage id="profile.dataExport.button.label"/>
          </button>

          <GrayCard>
            <CardSection hasBorder={false}>
              <Text.TextFootnote className="font-color-gsMedDark">
                <FormattedMessage id="profile.dataExport.message"/>
              </Text.TextFootnote>
            </CardSection>
          </GrayCard>

          {this.renderDeleteButton()}
        </ContentWrapper>
      </ContentLoader>
    );
  }
}

ProfileInfo.propTypes = {
  intl: PropTypes.object,
  phraseData: PropTypes.object,
  profile: PropTypes.instanceOf(ProfileModel).isRequired,
  gender: PropTypes.instanceOf(GenderModel).isRequired,
  getInitialData: PropTypes.func.isRequired,
  isMainProfile: PropTypes.bool,
  isShared: PropTypes.bool,
  deleteProfile: PropTypes.func,
  dispatch: PropTypes.func,
  actions: PropTypes.shape({
    selectPhoto: PropTypes.func.isRequired,
    deletePhoto: PropTypes.func.isRequired,
    confirmDeleteProfile: PropTypes.func,
    goToProfilePage: PropTypes.func.isRequired,
    selectMobilePhoto: PropTypes.func.isRequired,
  }),
};

export default connect()(injectIntl(ProfileInfo));
